import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { AlarmClock, BellRing, Check, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { CHECKLISTS_CHANGED, SNOOZES, announceChecklistsChanged, reminderLabel } from "@/lib/reminders";

const MAX_WAIT = 6 * 3600000;

// Shows a pop-up the moment a private to-do reminder is due (while the app is open).
// The server cron covers closed apps with a bell notification; both claim each reminder once.
export function TodoReminderHost() {
  const { user } = useAuth();
  const enabled = !!user;
  const [popups, setPopups] = useState([]);
  const timer = useRef(null);
  const navigate = useNavigate();
  // Sit above the to-do panel's footer button when the panel is open.
  const { pathname, search } = useLocation();
  const panelOpen = pathname.startsWith("/app/my-work") && search.includes("todos=1");

  const fireDue = useCallback(async () => {
    try {
      const { data } = await api.post("/checklists/reminders/due");
      if (data.length) {
        setPopups((p) => [...p.filter((x) => !data.some((d) => d.item_id === x.item_id)), ...data]);
        announceChecklistsChanged();
      }
    } catch { /* offline or signed out: the cron will still notify */ }
  }, []);

  const schedule = useCallback(async () => {
    clearTimeout(timer.current);
    try {
      const { data } = await api.get("/checklists");
      const next = data.flatMap((c) => c.items)
        .filter((i) => i.remind_at && !i.done && !i.reminded_at)
        .map((i) => new Date(i.remind_at).getTime())
        .sort((a, b) => a - b)[0];
      if (next == null) return;
      const wait = Math.min(Math.max(0, next - Date.now()), MAX_WAIT);
      timer.current = setTimeout(async () => { if (wait < MAX_WAIT) await fireDue(); schedule(); }, wait + 500);
    } catch { /* ignore */ }
  }, [fireDue]);

  useEffect(() => {
    if (!enabled) return undefined;
    fireDue().then(schedule);
    const onChange = () => schedule();
    const onVisible = () => { if (document.visibilityState === "visible") fireDue().then(schedule); };
    window.addEventListener(CHECKLISTS_CHANGED, onChange);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener(CHECKLISTS_CHANGED, onChange);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, fireDue, schedule]);

  const close = (p) => setPopups((list) => list.filter((x) => x.item_id !== p.item_id));
  const act = async (p, body, msg) => {
    close(p);
    try {
      const { data } = await api.patch(`/checklists/${p.checklist_id}/items/${p.item_id}`, body);
      announceChecklistsChanged();
      const item = data.items.find((i) => i.id === p.item_id);
      toast.success(body.done && item?.repeat ? `Done — next reminder ${reminderLabel(item.remind_at)}` : msg);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={`pointer-events-none fixed right-6 z-[80] flex w-[340px] flex-col gap-3 transition-[bottom] duration-300 ${panelOpen ? "bottom-24" : "bottom-6"}`} data-testid="todo-reminder-host">
      <AnimatePresence>
        {popups.map((p) => (
          <motion.div
            key={p.item_id}
            layout
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 40 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
            data-testid={`todo-reminder-popup-${p.item_id}`}
          >
            <div className="h-[3px] w-full bg-gradient-to-r from-[#FFB020] via-[#FFB020]/70 to-transparent" />
            <div className="p-5">
              <div className="flex items-start gap-3.5">
                <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFB020]/12 text-[#FFB020] ring-1 ring-inset ring-[#FFB020]/25">
                  <span className="absolute inset-0 animate-ping rounded-xl bg-[#FFB020]/10 [animation-duration:2.4s]" />
                  <BellRing className="relative h-[18px] w-[18px]" />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFB020]">Reminder</p>
                  <button onClick={() => { close(p); navigate("/app/my-work?todos=1"); }} className="mt-1 block text-left text-[15px] font-bold leading-snug text-white transition-colors hover:text-[#00FF66]" data-testid={`todo-reminder-text-${p.item_id}`}>
                    {p.text}
                  </button>
                  <p className="mt-1 truncate text-[11px] text-white/40">{p.checklist_title}</p>
                </div>
                <button onClick={() => close(p)} className="-mr-1 -mt-1 rounded-full p-1.5 text-white/30 transition-colors hover:bg-white/10 hover:text-white" aria-label="Dismiss" data-testid={`todo-reminder-dismiss-${p.item_id}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>

              <button onClick={() => act(p, { done: true }, "Marked done")} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#00FF66] py-2.5 text-xs font-black text-black transition-transform duration-200 hover:-translate-y-0.5" data-testid={`todo-reminder-done-${p.item_id}`}>
                <Check className="h-3.5 w-3.5" strokeWidth={3} /> Mark as done
              </button>

              <div className="mt-3 border-t border-white/[0.06] pt-3">
                <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/35">
                  <AlarmClock className="h-3 w-3" /> Snooze for
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {SNOOZES.map((s) => (
                    <button key={s.key} onClick={() => act(p, { remind_at: s.at().toISOString() }, `Snoozed: ${s.label}`)} className="rounded-lg border border-white/[0.08] bg-white/[0.03] py-2 text-[11px] font-bold text-white/70 transition-colors duration-150 hover:border-[#FFB020]/50 hover:bg-[#FFB020]/10 hover:text-[#FFB020]" data-testid={`todo-reminder-snooze-${s.key}-${p.item_id}`}>
                      {s.key === "tomorrow" ? "Tomorrow" : s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
